import React from 'react';

export interface JournalSuggestion {
  id: number;
  tekiyou: string;
  karikata_kamoku_id?: number | null;
  karikata_kamoku_name: string;
  kashikata_kamoku_id?: number | null;
  kashikata_kamoku_name: string;
  karikata_kingaku: number;
  kashikata_kingaku: number;
  kihyoubi: string;
  similarity_score?: number;
  frequency_score?: number;
  recency_score?: number;
  total_score?: number;
}

interface JournalEntryHistoryPanelProps {
  title: string;
  loading: boolean;
  error: string | null;
  suggestions: JournalSuggestion[];
  accountOptions: Array<{ value: number | null; label: string }>;
  selectedAccountId: number | null;
  selectedAccountName?: string;
  onAccountChange: (accountId: number | null) => void;
  onSelect: (suggestion: JournalSuggestion) => void;
  contextNote?: string;
}

const tableHeaderStyle: React.CSSProperties = {
  backgroundColor: '#d0d0d0',
  position: 'sticky',
  top: 0,
  zIndex: 2,
};

const JournalEntryHistoryPanel: React.FC<JournalEntryHistoryPanelProps> = ({
  title,
  loading,
  error,
  suggestions,
  accountOptions,
  selectedAccountId,
  selectedAccountName,
  onAccountChange,
  onSelect,
  contextNote,
}) => {
  const renderCounterparty = (suggestion: JournalSuggestion) => {
    if (!selectedAccountName) {
      return suggestion.karikata_kamoku_name || suggestion.kashikata_kamoku_name || '-';
    }
    if (suggestion.karikata_kamoku_name === selectedAccountName) {
      return suggestion.kashikata_kamoku_name || '-';
    }
    if (suggestion.kashikata_kamoku_name === selectedAccountName) {
      return suggestion.karikata_kamoku_name || '-';
    }
    return suggestion.karikata_kamoku_name || suggestion.kashikata_kamoku_name || '-';
  };

  const renderAmount = (suggestion: JournalSuggestion) => {
    let amount = suggestion.karikata_kingaku || suggestion.kashikata_kingaku || 0;
    if (selectedAccountName) {
      if (suggestion.karikata_kamoku_name === selectedAccountName) {
        amount = suggestion.karikata_kingaku || amount;
      } else if (suggestion.kashikata_kamoku_name === selectedAccountName) {
        amount = suggestion.kashikata_kingaku || amount;
      }
    }
    return `¥${amount.toLocaleString('ja-JP')}`;
  };

  const renderBody = () => {
    if (loading) {
      return (
        <tr>
          <td colSpan={4} style={{ padding: '10px', textAlign: 'center', fontStyle: 'italic' }}>
            候補を読み込み中です...
          </td>
        </tr>
      );
    }

    if (error) {
      return (
        <tr>
          <td
            colSpan={4}
            style={{
              padding: '10px',
              textAlign: 'center',
              color: '#cc0000',
              backgroundColor: '#ffe0e0',
            }}
          >
            {error}
          </td>
        </tr>
      );
    }

    if (suggestions.length === 0) {
      return (
        <tr>
          <td colSpan={4} style={{ padding: '10px', textAlign: 'center', color: '#666' }}>
            表示できる候補がありません
          </td>
        </tr>
      );
    }

    return suggestions.map((suggestion, idx) => {
      return (
        <tr
          key={`${suggestion.id}-${idx}`}
          style={{ cursor: 'pointer', backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f7f7f7' }}
          onClick={() => onSelect(suggestion)}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#e3eaff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#ffffff' : '#f7f7f7';
          }}
        >
          <td style={{ padding: '6px', borderBottom: '1px solid #dcdcdc' }}>
            {suggestion.kihyoubi}
          </td>
          <td
            style={{
              padding: '6px',
              borderBottom: '1px solid #dcdcdc',
              textAlign: 'right',
            }}
          >
            {renderAmount(suggestion)}
          </td>
          <td style={{ padding: '6px', borderBottom: '1px solid #dcdcdc' }}>
            {renderCounterparty(suggestion)}
          </td>
          <td style={{ padding: '6px', borderBottom: '1px solid #dcdcdc' }}>
            {suggestion.tekiyou}
          </td>
        </tr>
      );
    });
  };

  return (
    <div
      style={{
        backgroundColor: '#f0f0f0',
        border: '2px inset #f0f0f0',
        padding: '0',
      }}
    >
      <div
        style={{
          backgroundColor: '#0066cc',
          color: '#ffffff',
          padding: '8px 12px',
          fontWeight: 'bold',
        }}
      >
        {title}
      </div>
      {contextNote && (
        <div
          style={{
            padding: '8px 12px',
            fontSize: '10px',
            color: '#333',
            backgroundColor: '#f9f9f9',
            borderBottom: '1px solid #cccccc',
            lineHeight: 1.4,
          }}
        >
          {contextNote}
        </div>
      )}
      <div
        style={{
          padding: '8px 12px',
          fontSize: '11px',
          color: '#333',
          backgroundColor: '#f0f0f0',
          borderBottom: '1px solid #c0c0c0',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <span style={{ width: '88px' }}>表示対象科目</span>
        <select
          value={selectedAccountId ?? ''}
          onChange={(event) => {
            const value = event.target.value;
            onAccountChange(value === '' ? null : Number(value));
          }}
          style={{
            padding: '2px 4px',
            border: '2px inset #c0c0c0',
            fontSize: '11px',
            fontFamily: 'MS UI Gothic, sans-serif',
            backgroundColor: '#ffffff',
            flex: 1,
          }}
        >
          {accountOptions.map((option) => (
            <option key={`history-option-${option.value ?? 'all'}`} value={option.value ?? ''}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      <div style={{ maxHeight: '360px', overflowY: 'auto', borderTop: '1px solid #cccccc' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '11px',
          }}
        >
          <thead>
            <tr style={tableHeaderStyle}>
              <th style={{ padding: '6px', borderBottom: '2px solid #808080' }}>日付</th>
              <th style={{ padding: '6px', borderBottom: '2px solid #808080', textAlign: 'right' }}>
                金額
              </th>
              <th style={{ padding: '6px', borderBottom: '2px solid #808080' }}>相手科目</th>
              <th style={{ padding: '6px', borderBottom: '2px solid #808080' }}>摘要</th>
            </tr>
          </thead>
          <tbody>{renderBody()}</tbody>
        </table>
      </div>
      <div
        style={{
          padding: '6px 10px',
          fontSize: '10px',
          color: '#555',
          borderTop: '1px solid #cccccc',
          backgroundColor: '#f7f7f7',
        }}
      >
        {suggestions.length}件の候補 | 行をクリックすると仕訳に反映されます
      </div>
    </div>
  );
};

export default JournalEntryHistoryPanel;
